import { defineStore } from "pinia";
import dayjs from "dayjs";
import router from "@/router";
import FetchService from "@/services/fetch.service";
import { useConferenceStore } from "../teacher/conference";
import { sort } from "@/utils/sort";
import type { ConferenceDay, GetConference } from "@/api_types/teacher";
type Break = {
  start_time: Date;
  end_time: Date;
  has_teachers: boolean;
  has_teams: boolean;
};
const steps = [
  {
    title: "Event",
    component: "EventTab",
    finished() {
      const w: any = useWizard();
      return !!(w.name?.length && w.name.length >= 3);
    },
    icon: "mdi-calendar-blank",
    message: "Event Name needs to have at least 3 characters"
  },
  {
    title: "Days",
    component: "DaysTab",
    finished() {
      const w: any = useWizard();
      return w.days.length === w.eventDays;
    },
    icon: "mdi-calendar-today",
    message: "Make sure to add days as the specified number"
  },
  {
    title: "Schedules",
    component: "SchedulesTab",
    finished() {
      const w: any = useWizard();
      return w.allTeachers || w.allTeams;
    },
    icon: "mdi-cog",
    message: "At least one of the options need to be selected"
  },
  {
    title: "Breaks",
    component: "BreaksTab",
    finished() {
      const w: any = useWizard();
      return w.steps[2].finished();
    },
    icon: "mdi-coffee",
    message: ""
  }
] as const;

export type EventDay = {
  start_time: Date;
  end_time: Date;
  interval: number;
  breaks: Break[];
};

export type SubmitMessage = {
  status: "success" | "loading" | "error";
  message: string;
};
export const useWizard = defineStore("school/wizard", {
  state: () => {
    const cSteps = steps.map((i) => ({ ...i }));
    return {
      step: cSteps[0],
      steps: cSteps,
      name: "",
      eventDays: 1,
      days: [] as EventDay[],
      newDay: {
        start_time: dayjs().set("hours", 8).set("minutes", 0).toDate(),
        end_time: dayjs().set("hours", 17).set("minutes", 0).toDate(),
        interval: 15
      },
      allTeachers: false,
      allTeams: false,
      loading: false,
      showModal: false,
      submitMessages: [] as SubmitMessage[],
      error: false,
      savedDays: [] as { index: number; day: ConferenceDay }[]
    };
  },
  actions: {
    saveDay() {
      this.days.push({ ...this.newDay, breaks: [] });
      this.newDay = {
        start_time: dayjs().set("hours", 8).set("minutes", 0).toDate(),
        end_time: dayjs().set("hours", 17).set("minutes", 0).toDate(),
        interval: 15
      };
    },
    async submit() {
      this.loading = true;
      this.showModal = true;
      this.submitMessages = [];
      this.submitMessages.push({
        status: "loading",
        message: "Creating event"
      });
      let event_id = 0;
      const confStore = useConferenceStore();

      const days = sort(this.days, (i) => i.start_time.toISOString());
      const firstDay = days[0];
      try {
        const event = (await confStore.addConference({
          conference_name: this.name,
          open_tstamp: dayjs(firstDay.start_time)
            .subtract(2, "weeks")
            .set("hours", 8)
            .set("minutes", 0)
            .set("seconds", 0)
            .format(`YYYY-MM-DD HH:mm:ss`),
          closed_tstamp: dayjs(firstDay.start_time)
            .subtract(1, "days")
            .set("hours", 12)
            .set("minutes", 0)
            .set("seconds", 0)
            .format(`YYYY-MM-DD HH:mm:ss`)
        })) as GetConference;
        if (event?.conference_id) {
          event_id = event.conference_id;
          this.submitMessages.at(-1)!.status = "success";
          for (let i = 0; i < days.length; i++) {
            const day = days[i];
            this.submitMessages.push({
              status: "loading",
              message: `Adding event day ${i + 1}`
            });
            const end = dayjs(day.end_time);
            const eventDay = (await FetchService.fetchData("/conference-days", "post", {
              start: dayjs(day.start_time).set("seconds", 0).format(`YYYY-MM-DD HH:mm:ss`),
              end: dayjs(day.start_time)
                .set("hours", end.hour())
                .set("minutes", end.minute())
                .set("seconds", 0)
                .format(`YYYY-MM-DD HH:mm:ss`),
              time_interval: day.interval,
              conference_id: event.conference_id,
              meeting_types: {
                in_person: true,
                virtual: false,
                callback: false,
                phone: false,
                virtual_type: "url"
              }
            })) as ConferenceDay;
            if (eventDay?.conference_days_id) {
              this.submitMessages.at(-1)!.status = "success";
              confStore.updatedConference(event.conference_id, {
                conference_days: [
                  ...(confStore.getById(event.conference_id)?.conference_days || []),
                  eventDay
                ]
              });
              // add teachers/teams
              let m = "Adding ";
              if (this.allTeachers) m += "teachers" + (this.allTeams ? " and teams" : "");
              else m += "teams";
              m += " for day " + (i + 1);
              this.submitMessages.push({
                status: "loading",
                message: m
              });
              const res = await FetchService.fetchData(
                `/conference-days/entities-bulk/${eventDay.conference_days_id}?teachers=${this.allTeachers}&teams=${this.allTeams}`,
                "post"
              );

              if (res?.success) {
                this.submitMessages.at(-1)!.status = "success";
                if (day.breaks.length) {
                  // add breaks
                  this.submitMessages.push({
                    status: "loading",
                    message: "Adding Breaks for day " + (i + 1)
                  });
                  const breaks = day.breaks.map((i) => {
                    const start = dayjs(i.start_time);
                    const end = dayjs(i.end_time);
                    return {
                      start_time: dayjs(day.start_time)
                        .set("hours", start.hour())
                        .set("minutes", start.minute())
                        .set("seconds", 0)
                        .format(`YYYY-MM-DD HH:mm:ss`),
                      end_time: dayjs(day.start_time)
                        .set("hours", end.hour())
                        .set("minutes", end.minute())
                        .set("seconds", 0)
                        .format(`YYYY-MM-DD HH:mm:ss`),
                      conference_days_id: eventDay.conference_days_id,
                      has_teachers: i.has_teachers,
                      has_teams: i.has_teams
                    };
                  });
                  let breaksSuccess = true;
                  for (let i = 0; i < breaks.length; i++) {
                    const breakRes = await FetchService.fetchData(
                      `/conference-breaks`,
                      "post",
                      breaks[i]
                    );
                    if (!breakRes?.success) {
                      breaksSuccess = false;
                      break;
                    }
                  }

                  if (breaksSuccess) {
                    this.submitMessages.at(-1)!.status = "success";
                  } else {
                    this.submitMessages.at(-1)!.status = "error";
                    throw "Error while trying to add breaks";
                  }
                }
              } else {
                this.submitMessages.at(-1)!.status = "error";
                throw "Error while trying to add teachers/teams";
              }
            } else {
              this.submitMessages.at(-1)!.status = "error";
              throw "Error while trying to add event day";
            }
          }
        } else {
          this.submitMessages.at(-1)!.status = "error";
          throw "Error while trying to add event";
        }
        this.submitMessages.push({
          status: "success",
          message: "Event Added Successfully"
        });
      } catch (error: any) {
        this.error = true;
        this.submitMessages.push({
          status: "loading",
          message: error?.message || error
        });
        if (event_id) {
          await confStore.deleteConference(event_id);
        }
        this.submitMessages.at(-1)!.status = "error";
      }
      this.loading = false;
    },
    close() {
      this.showModal = false;
      if (!this.error) {
        this.$reset();
        router.push({
          name: "school-events"
        });
      }
    }
  }
});

export default useWizard;
